import { FC, Fragment, useState, useEffect, useRef } from "react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import emojiFlags from "emoji-flags";
import { t } from "i18next";
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";

import { SpanIconCheckSelect } from "../atoms/SpanIconCheckSelect";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";

type option = { id: number | string; name: string; code: string };
export type Selectprops = {
  selected: option;
  setSelected: (option: option) => void;
  options: option[];
  tittle: string;
  textDescribe?: string;
  span?: string;
};

export const SelectCountry: FC<Selectprops> = ({
  selected,
  setSelected,
  options = [], // Default to empty array if options is undefined
  tittle,
  textDescribe,
  span,
}) => {
  const [search, setSearch] = useState("");
  const [showAbove, setShowAbove] = useState(false);
  const listboxRef = useRef<HTMLDivElement>(null);
  const defaultOption = { id: "default", name: "Seleccione un país", code: "" };
  const optionsWithDefault = [defaultOption, ...(Array.isArray(options) ? options : [])];

  const filteredOptions = optionsWithDefault
    ?.filter((option) => option.name.toLowerCase().includes(search.toLowerCase()))
    ?.sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    const handleScroll = () => {
      if (listboxRef.current) {
        const rect = listboxRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        setShowAbove(rect.bottom > windowHeight);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <Fragment>
      <Listbox value={selected} onChange={setSelected}>
        <div ref={listboxRef}>
          <div className="flex justify-between">
            <Label className="block text-sm font-medium leading-6 text-gray-600">
              {tittle}
            </Label>
            <span className={`text-sm leading-6 text-gray-500`}>{span}</span>
          </div>

          <div className="relative">
            <ListboxButton className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6">
              <span className="flex items-center truncate">
                {selected?.code
                  ? `${emojiFlags.countryCode(selected.code).emoji} ${selected.name}`
                  : defaultOption.name}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="h-5 w-5 text-gray-400"
                />
              </span>
            </ListboxButton>

            <ListboxOptions
              transition
              className={`absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm ${
                showAbove ? "bottom-full mb-1" : "top-full mt-1"
              }`}
            >
              <div className="px-3 py-2">
                <InputWithCornerHint
                  type="text"
                  placeholder={t(
                    "nutrition-wizard.views.search_by_name_placeholder"
                  )}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  label=""
                />
              </div>
              {filteredOptions.length > 0 ? (
                filteredOptions?.map((person) => (
                  <ListboxOption
                    key={person.id}
                    value={person}
                    className="group relative cursor-default select-none py-2 pl-8 pr-4 text-gray-300 data-[focus]:bg-primary-600 data-[focus]:text-white"
                  >
                    <span className="flex items-center truncate font-normal group-data-[selected]:font-semibold text-gray-900">
                      {person.name}{" "}
                      {person.code && emojiFlags.countryCode(person.code).emoji}
                    </span>
                    <SpanIconCheckSelect />
                  </ListboxOption>
                ))
              ) : (
                <div className="px-3 py-2 text-gray-500">
                  {t("share.selectpicker.no_results")}
                </div>
              )}
            </ListboxOptions>
          </div>

          <p className="mt-1 text-sm text-gray-400">{textDescribe}</p>
        </div>
      </Listbox>
    </Fragment>
  );
};